import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Tag, Package, Receipt, Settings as SettingsIcon, ExternalLink } from "lucide-react";

const CATEGORIES = [
  { value: "vetements", label: "Vêtements" },
  { value: "batons", label: "Bâtons" },
  { value: "accessoires", label: "Accessoires" },
];
const STATUS_LABELS = {
  pending: "En attente",
  paid: "Payée",
  shipped: "Expédiée",
  completed: "Terminée",
  cancelled: "Annulée",
};
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  shipped: "bg-blue-100 text-blue-800",
  completed: "bg-slate-100 text-slate-800",
  cancelled: "bg-red-100 text-red-800",
};

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ---------- Products tab ----------
function ProductsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // product or null
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/admin/shop/products").then(r => setItems(r.data)).catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const empty = { name: "", description: "", price_eur: 0, category: "vetements", image_url: "", active: true };
  const [form, setForm] = useState(empty);
  const openNew = () => { setForm(empty); setEditing(null); setShowForm(true); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p); setShowForm(true); };
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, price_eur: parseFloat(form.price_eur) || 0 };
    try {
      if (editing) await api.put(`/admin/shop/products/${editing.id}`, payload);
      else await api.post("/admin/shop/products", payload);
      toast.success(editing ? "Produit mis à jour" : "Produit créé");
      setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » ?`)) return;
    try { await api.delete(`/admin/shop/products/${p.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>Produits ({items.length})</h2>
        <button onClick={openNew} className="btn-primary text-sm" data-testid="admin-shop-product-new"><Plus size={14}/> Ajouter</button>
      </div>
      {showForm && (
        <form onSubmit={submit} className="card-warm p-6 mb-8 grid md:grid-cols-2 gap-3" data-testid="admin-shop-product-form">
          <input required className="input-field" placeholder="Nom du produit *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="admin-product-name" />
          <div className="flex gap-2">
            <input required type="number" step="0.01" min="0" className="input-field flex-1" placeholder="Prix TTC *" value={form.price_eur} onChange={e => setForm({ ...form, price_eur: e.target.value })} data-testid="admin-product-price" />
            <span className="self-center">€</span>
          </div>
          <select className="input-field" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="admin-product-category">
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <input className="input-field" placeholder="URL de l'image (https://…)" value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} data-testid="admin-product-image" />
          <textarea className="input-field md:col-span-2" rows={3} placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="admin-product-desc" />
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} data-testid="admin-product-active" /> Produit actif (visible en boutique)
          </label>
          <div className="md:col-span-2 flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost border text-sm">Annuler</button>
            <button type="submit" className="btn-primary text-sm" data-testid="admin-product-save"><Check size={14}/> Enregistrer</button>
          </div>
        </form>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(p => (
          <div key={p.id} className="card-warm overflow-hidden flex flex-col" data-testid={`admin-product-${p.id}`}>
            <div className="aspect-[4/3] bg-[var(--forest-light)] overflow-hidden relative">
              {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> :
                <div className="w-full h-full flex items-center justify-center text-[var(--muted-text)]"><Package size={40}/></div>}
              {!p.active && <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs">Inactif</span>}
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <span className="text-xs uppercase text-[var(--muted-text)]">{CATEGORIES.find(c => c.value === p.category)?.label}</span>
              <div className="font-serif-display text-lg mt-1" style={{ color: "var(--pine)" }}>{p.name}</div>
              <div className="font-semibold text-[var(--terracotta)] mt-1">{p.price_eur.toFixed(2)} €</div>
              <div className="flex gap-2 mt-auto pt-3">
                <button onClick={() => openEdit(p)} className="btn-ghost border text-xs flex-1" data-testid={`admin-product-edit-${p.id}`}><Pencil size={12}/> Modifier</button>
                <button onClick={() => remove(p)} className="text-red-600 hover:text-red-700 text-xs px-2" data-testid={`admin-product-delete-${p.id}`}><Trash2 size={14}/></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-[var(--muted-text)] py-12">Aucun produit — cliquez « Ajouter » pour commencer.</div>}
      </div>
    </div>
  );
}

// ---------- Orders tab ----------
function OrdersTab() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const load = () => api.get("/admin/shop/orders" + (statusFilter ? `?status=${statusFilter}` : "")).then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [statusFilter]);
  const updateStatus = async (o, field, value) => {
    try { await api.patch(`/admin/shop/orders/${o.id}`, { [field]: value }); toast.success("Mis à jour"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Supprimer la commande ${o.order_number} ?`)) return;
    try { await api.delete(`/admin/shop/orders/${o.id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>Commandes ({items.length})</h2>
        <div className="flex gap-2 flex-wrap">
          {["", "pending", "paid", "shipped", "completed", "cancelled"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 rounded-full text-xs ${statusFilter === s ? "bg-[var(--pine)] text-white" : "bg-white border text-[var(--muted-text)]"}`}
              data-testid={`admin-orders-filter-${s || "all"}`}>
              {s === "" ? "Toutes" : STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        {items.map(o => (
          <div key={o.id} className="card-warm p-4" data-testid={`admin-order-${o.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-semibold" style={{ color: "var(--pine)" }}>{o.order_number} — {o.customer_name}</div>
                <div className="text-xs text-[var(--muted-text)]">{fmtDate(o.created_at)} · <a href={`mailto:${o.customer_email}`} className="underline">{o.customer_email}</a>{o.customer_phone ? ` · ${o.customer_phone}` : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[o.status]}`}>{STATUS_LABELS[o.status]}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                  {o.payment_status === "paid" ? "Payé" : "Impayé"}
                </span>
                <span className="font-semibold text-[var(--terracotta)]">{o.total_eur.toFixed(2)} €</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3 items-center text-xs">
              <span className="text-[var(--muted-text)]">{o.delivery_method === "pickup" ? "Retrait" : "Livraison postale"} · Paiement {o.payment_method}</span>
              <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="btn-ghost border text-xs" data-testid={`admin-order-expand-${o.id}`}>{expanded === o.id ? "Masquer" : "Détails"}</button>
              <select value={o.status} onChange={e => updateStatus(o, "status", e.target.value)} className="input-field text-xs !py-1" data-testid={`admin-order-status-${o.id}`}>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              {o.payment_status !== "paid" ? (
                <button onClick={() => updateStatus(o, "payment_status", "paid")} className="btn-secondary text-xs" data-testid={`admin-order-paid-${o.id}`}>Marquer payé</button>
              ) : (
                <button onClick={() => updateStatus(o, "payment_status", "unpaid")} className="btn-ghost border text-xs">Réouvrir paiement</button>
              )}
              <button onClick={() => remove(o)} className="text-red-600 hover:text-red-700 ml-auto text-xs"><Trash2 size={14} className="inline"/></button>
            </div>
            {expanded === o.id && (
              <div className="mt-4 pt-4 border-t border-slate-100 grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-semibold text-xs uppercase text-[var(--muted-text)] mb-2">Articles</div>
                  {o.items.map((it, i) => (
                    <div key={i} className="flex justify-between py-1"><span>{it.name} × {it.quantity}</span><span>{it.line_total_eur.toFixed(2)} €</span></div>
                  ))}
                  <div className="mt-2 pt-2 border-t text-xs space-y-1">
                    <div className="flex justify-between"><span>Sous-total</span><span>{o.subtotal_eur.toFixed(2)} €</span></div>
                    <div className="flex justify-between"><span>Livraison</span><span>{o.shipping_fee_eur.toFixed(2)} €</span></div>
                    {o.discount_amount_eur > 0 && <div className="flex justify-between text-[var(--terracotta)]"><span>Remise ({o.discount_code})</span><span>- {o.discount_amount_eur.toFixed(2)} €</span></div>}
                    <div className="flex justify-between font-semibold"><span>Total</span><span>{o.total_eur.toFixed(2)} €</span></div>
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-xs uppercase text-[var(--muted-text)] mb-2">Livraison</div>
                  {o.delivery_method === "postal" ? (
                    <div className="text-sm">{o.address}<br/>{o.zip_code} {o.city}</div>
                  ) : (
                    <div className="text-[var(--muted-text)] text-sm">Retrait sur place</div>
                  )}
                  {o.notes && <div className="mt-3"><div className="font-semibold text-xs uppercase text-[var(--muted-text)]">Remarque</div><p className="text-sm mt-1">{o.notes}</p></div>}
                </div>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-[var(--muted-text)] py-12">Aucune commande.</div>}
      </div>
    </div>
  );
}

// ---------- Discounts tab ----------
function DiscountsTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { code: "", kind: "percent", value: 10, active: true, max_uses: 0, valid_until: "" };
  const [form, setForm] = useState(empty);
  const load = () => api.get("/admin/shop/discounts").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const openNew = () => { setForm(empty); setEditing(null); setShowForm(true); };
  const openEdit = (d) => {
    setForm({
      code: d.code, kind: d.kind, value: d.value, active: d.active,
      max_uses: d.max_uses || 0,
      valid_until: d.valid_until ? d.valid_until.slice(0, 10) : "",
    }); setEditing(d); setShowForm(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      code: form.code.trim().toUpperCase(),
      kind: form.kind,
      value: parseFloat(form.value) || 0,
      active: form.active,
      max_uses: parseInt(form.max_uses) || 0,
      valid_until: form.valid_until ? new Date(form.valid_until + "T23:59:59Z").toISOString() : null,
    };
    try {
      if (editing) await api.put(`/admin/shop/discounts/${editing.id}`, payload);
      else await api.post("/admin/shop/discounts", payload);
      toast.success(editing ? "Code mis à jour" : "Code créé");
      setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Supprimer le code ${d.code} ?`)) return;
    try { await api.delete(`/admin/shop/discounts/${d.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif-display text-2xl" style={{ color: "var(--pine)" }}>Codes de remise ({items.length})</h2>
        <button onClick={openNew} className="btn-primary text-sm" data-testid="admin-discount-new"><Plus size={14}/> Ajouter</button>
      </div>
      {showForm && (
        <form onSubmit={submit} className="card-warm p-6 mb-6 grid sm:grid-cols-2 gap-3" data-testid="admin-discount-form">
          <input required className="input-field uppercase" placeholder="Code *" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="admin-discount-code" />
          <select className="input-field" value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })} data-testid="admin-discount-kind">
            <option value="percent">Pourcentage (%)</option>
            <option value="fixed">Montant fixe (€)</option>
          </select>
          <input required type="number" step="0.01" min="0" className="input-field" placeholder="Valeur *" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} data-testid="admin-discount-value" />
          <input type="number" min="0" className="input-field" placeholder="Nb max d'utilisations (0 = illimité)" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })} data-testid="admin-discount-maxuses" />
          <input type="date" className="input-field" placeholder="Valable jusqu'à" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} data-testid="admin-discount-until" />
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} data-testid="admin-discount-active" /> Code actif
          </label>
          <div className="sm:col-span-2 flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost border text-sm">Annuler</button>
            <button type="submit" className="btn-primary text-sm"><Check size={14}/> Enregistrer</button>
          </div>
        </form>
      )}
      <div className="space-y-2">
        {items.map(d => (
          <div key={d.id} className="card-warm p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`admin-discount-${d.code}`}>
            <div className="flex items-center gap-3">
              <Tag size={18} className="text-[var(--terracotta)]"/>
              <div>
                <div className="font-mono font-semibold" style={{ color: "var(--pine)" }}>{d.code}</div>
                <div className="text-xs text-[var(--muted-text)]">
                  {d.kind === "percent" ? `${d.value}% de remise` : `${d.value.toFixed(2)} € fixes`} ·
                  {d.max_uses > 0 ? ` ${d.uses_count}/${d.max_uses} usages` : ` ${d.uses_count} usages`}
                  {d.valid_until ? ` · exp. ${fmtDate(d.valid_until)}` : ""}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs ${d.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>{d.active ? "Actif" : "Désactivé"}</span>
              <button onClick={() => openEdit(d)} className="btn-ghost border text-xs" data-testid={`admin-discount-edit-${d.code}`}><Pencil size={12}/></button>
              <button onClick={() => remove(d)} className="text-red-600 hover:text-red-700"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-[var(--muted-text)] py-12">Aucun code — créez-en un pour vos campagnes promo.</div>}
      </div>
    </div>
  );
}

// ---------- Settings tab ----------
function ShopSettingsTab() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/shop/settings").then(r => setForm(r.data)).catch(() => {});
  }, []);
  if (!form) return <div className="text-[var(--muted-text)]">Chargement…</div>;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.put("/admin/shop/settings", {
        shipping_fee_eur: parseFloat(form.shipping_fee_eur) || 0,
        free_shipping_threshold_eur: parseFloat(form.free_shipping_threshold_eur) || 0,
        pickup_instructions: form.pickup_instructions || "",
        postal_instructions: form.postal_instructions || "",
        bank_iban: form.bank_iban || "",
        bank_beneficiary: form.bank_beneficiary || "",
      });
      toast.success("Réglages enregistrés");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <h2 className="font-serif-display text-2xl mb-6" style={{ color: "var(--pine)" }}>Réglages boutique</h2>
      <form onSubmit={submit} className="card-warm p-6 max-w-2xl space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[var(--muted-text)] uppercase">Frais de livraison (€)</label>
            <input type="number" step="0.01" min="0" className="input-field mt-1" value={form.shipping_fee_eur} onChange={e => setForm({ ...form, shipping_fee_eur: e.target.value })} data-testid="admin-shop-shipping" />
          </div>
          <div>
            <label className="text-xs text-[var(--muted-text)] uppercase">Livraison offerte dès (€)</label>
            <input type="number" step="0.01" min="0" className="input-field mt-1" value={form.free_shipping_threshold_eur} onChange={e => setForm({ ...form, free_shipping_threshold_eur: e.target.value })} data-testid="admin-shop-threshold" />
          </div>
        </div>
        <div>
          <label className="text-xs text-[var(--muted-text)] uppercase">Instructions retrait</label>
          <textarea rows={2} className="input-field mt-1" value={form.pickup_instructions} onChange={e => setForm({ ...form, pickup_instructions: e.target.value })} data-testid="admin-shop-pickup" />
        </div>
        <div>
          <label className="text-xs text-[var(--muted-text)] uppercase">Instructions livraison postale</label>
          <textarea rows={2} className="input-field mt-1" value={form.postal_instructions} onChange={e => setForm({ ...form, postal_instructions: e.target.value })} data-testid="admin-shop-postal" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[var(--muted-text)] uppercase">IBAN (virement)</label>
            <input className="input-field mt-1" value={form.bank_iban} onChange={e => setForm({ ...form, bank_iban: e.target.value })} placeholder="BE00 0000 0000 0000" data-testid="admin-shop-iban" />
          </div>
          <div>
            <label className="text-xs text-[var(--muted-text)] uppercase">Bénéficiaire</label>
            <input className="input-field mt-1" value={form.bank_beneficiary} onChange={e => setForm({ ...form, bank_beneficiary: e.target.value })} data-testid="admin-shop-bank-name" />
          </div>
        </div>
        <button type="submit" disabled={busy} className="btn-primary" data-testid="admin-shop-settings-save"><Check size={14}/> Enregistrer</button>
      </form>
    </div>
  );
}

// ---------- Root ----------
export default function AdminShop() {
  const [tab, setTab] = useState("products");
  const TABS = [
    { key: "products", label: "Produits", icon: Package },
    { key: "orders", label: "Commandes", icon: Receipt },
    { key: "discounts", label: "Codes remise", icon: Tag },
    { key: "settings", label: "Réglages", icon: SettingsIcon },
  ];
  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-serif-display text-4xl font-semibold" style={{ color: "var(--pine)" }}>Boutique</h1>
          <p className="text-[var(--muted-text)] mt-1">Vêtements et équipements de Marche Nordique.</p>
        </div>
        <a href="/boutique" target="_blank" rel="noreferrer" className="btn-ghost border text-sm inline-flex items-center gap-1.5" data-testid="admin-shop-preview">
          Voir la boutique <ExternalLink size={14}/>
        </a>
      </div>
      <div className="flex gap-1 mb-8 border-b border-slate-200 flex-wrap">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm inline-flex items-center gap-1.5 -mb-px border-b-2 ${tab === t.key ? "border-[var(--terracotta)] text-[var(--pine)]" : "border-transparent text-[var(--muted-text)] hover:text-[var(--pine)]"}`}
            data-testid={`admin-shop-tab-${t.key}`}>
            <t.icon size={14}/> {t.label}
          </button>
        ))}
      </div>
      {tab === "products" && <ProductsTab />}
      {tab === "orders" && <OrdersTab />}
      {tab === "discounts" && <DiscountsTab />}
      {tab === "settings" && <ShopSettingsTab />}
    </div>
  );
}
